import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Tooltip } from "antd";
import {
  CloseOutlined,
  DownOutlined,
  ExpandAltOutlined,
  FormOutlined,
} from "@ant-design/icons";

import { useHideIntercom } from "../../hooks/useHideIntercom";
import { useRecentConversations } from "../../navigation/AppNavigation/useRecentConversations";
import NeoChatView from "./NeoChatView";
import { useNeoDrawer } from "./NeoDrawerContext";
import RecentChatsMenu from "./RecentChatsMenu";
import styles from "./styles.module.scss";

/**
 * ui2026 "Ask Circadia AI": a Copilot conversation docked beside the page.
 * The conversation itself is NeoChatView (shared with the Predict flag peek);
 * this is the shell: chat switcher, new chat, expand to full Copilot, close.
 */
const NeoDrawer = () => {
  const drawer = useNeoDrawer();
  const navigate = useNavigate();
  const location = useLocation();
  const asideRef = useRef<HTMLElement>(null);

  const open = !!drawer?.open;
  const session = drawer?.session;
  const { conversations, loading: recentLoading } =
    useRecentConversations(open);

  // Escape closes the panel only while focus is inside it, so it never
  // fights page-level overlays (side peeks, modals) for the key.
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (
        event.key === "Escape" &&
        asideRef.current?.contains(document.activeElement)
      ) {
        drawer?.close();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, drawer]);

  // The full Copilot page is already the expanded view of this panel.
  const onCopilotPage = location.pathname.startsWith("/copilot");
  const visible = !!drawer && open && !!session && !onCopilotPage;

  // The Intercom bubble sits on top of the panel's send button.
  useHideIntercom(visible);

  if (!visible || !drawer || !session) return null;

  const expand = () => {
    const id = session.conversationId;
    navigate(
      id ? `/copilot?conversation=${id}` : `/copilot?new=${Date.now()}`,
      {
        state: session.workflow ? { workflow: session.workflow } : null,
      }
    );
    drawer.close();
  };

  return (
    <aside
      ref={asideRef}
      className={`${styles.drawer} ${
        session.conversationId != null ? styles.drawerConversationStarted : ""
      }`}
      aria-label="Ask Circadia AI"
      data-conversation-started={session.conversationId != null}
    >
      <NeoChatView
        sessionKey={session.sessionKey}
        conversationId={session.conversationId}
        initialPrompt={session.initialPrompt}
        onInitialPromptConsumed={drawer.consumeInitialPrompt}
        onConversationCreated={drawer.adoptConversation}
        pendingLabel={session.pendingLabel ?? null}
        workflow={session.workflow ?? null}
        renderHeader={({ title }) => (
          <header className={styles.header}>
            <RecentChatsMenu
              conversations={conversations}
              loading={recentLoading}
              activeId={session.conversationId}
              onSelect={(id) => drawer.openConversation(id)}
              onNewChat={() => drawer.openNewChat()}
            >
              <button
                type="button"
                className={styles.titleButton}
                aria-label="Switch chat"
              >
                <span className={styles.titleText}>{title}</span>
                <DownOutlined className={styles.titleChevron} />
              </button>
            </RecentChatsMenu>
            <div className={styles.headerActions}>
              <Tooltip title="New chat">
                <button
                  type="button"
                  className={styles.iconButton}
                  onClick={() => drawer.openNewChat()}
                  aria-label="New chat"
                >
                  <FormOutlined />
                </button>
              </Tooltip>
              <Tooltip title="Open in full view">
                <button
                  type="button"
                  className={styles.iconButton}
                  onClick={expand}
                  aria-label="Open in full view"
                >
                  <ExpandAltOutlined />
                </button>
              </Tooltip>
              <Tooltip title="Close">
                <button
                  type="button"
                  className={styles.iconButton}
                  onClick={drawer.close}
                  aria-label="Close Ask Circadia AI"
                >
                  <CloseOutlined />
                </button>
              </Tooltip>
            </div>
          </header>
        )}
      />
    </aside>
  );
};

export default NeoDrawer;
